import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FileText } from "lucide-react";

const TermsPage = () => {
  const { i18n, t } = useTranslation();
  const showEnDisclaimer = (i18n.language || "").startsWith("en");
  return (
    <div className="min-h-screen py-20 px-6">
      <div className="container mx-auto max-w-4xl">

        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-10 h-10 text-gold" />

            <h1 className="font-display text-4xl md:text-5xl text-gold">
              {showEnDisclaimer ? "Terms of Service" : "Termini di Servizio"}
            </h1>
          </div>

          <p className="text-muted-foreground">
            {showEnDisclaimer ? "Last updated: 08/11/2026" : "Ultimo aggiornamento: 11/08/2026"}
          </p>

          {showEnDisclaimer && (
            <div className="mt-6 p-4 rune-border bg-gold/10 border border-gold rounded-lg" data-testid="legal-en-notice">
              <p className="font-heading text-sm text-gold mb-2">
                {t("legal.onlyItalianTitle")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("legal.onlyItalianBody")}
              </p>
            </div>
          )}
        </div>

        <div className="space-y-8 text-muted-foreground leading-relaxed">

          {/* 1 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              1. Oggetto e Accettazione dei Termini
            </h2>

            <p>
              I presenti Termini di Servizio ("Termini") disciplinano
              l'accesso e l'utilizzo del sito web mytharion.app e dei
              relativi software, servizi digitali, prodotti, funzionalità
              e contenuti (collettivamente, il "Servizio").
            </p>

            <p className="mt-3">
              Utilizzando il Servizio, creando un account o effettuando
              un acquisto, l'utente dichiara di aver letto e accettato
              i presenti Termini.
            </p>

            <p className="mt-3">
              Se non accetti i presenti Termini, non utilizzare il Servizio.
            </p>

            <p className="mt-3">
              Qualora l'utente agisca in qualità di consumatore, i diritti
              inderogabili riconosciuti dalla normativa applicabile
              prevalgono sulle eventuali disposizioni dei presenti Termini
              incompatibili con tali diritti.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              2. Mytharion.app
            </h2>

            <p>
              Mytharion.app è un'impresa gestita da una sola persona,
              con sede in Napoli, Italia, che sviluppa e offre software
              e servizi digitali per giochi di ruolo fantasy.
            </p>

            <p className="mt-3">
              Per comunicazioni relative ai presenti Termini:
            </p>

            <p className="mt-3">
              <strong>Email:</strong>{" "}
              <a
                href="mailto:legal@mytharion.app"
                className="text-gold hover:underline"
              >
                legal@mytharion.app
              </a>
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              3. Descrizione del Servizio
            </h2>

            <p>
              Mytharion fornisce software, strumenti e servizi digitali
              destinati principalmente ai giochi di ruolo fantasy.
              Tra questi può essere incluso Cartomancer e le relative
              funzionalità.
            </p>

            <p className="mt-3">
              Il Servizio può comprendere prodotti acquistabili una tantum,
              abbonamenti ricorrenti, funzionalità gratuite, periodi di
              prova, aggiornamenti e nuove funzionalità.
            </p>

            <p className="mt-3">
              Le caratteristiche e le condizioni economiche applicabili
              a ciascun prodotto o servizio sono quelle indicate nella
              relativa pagina di acquisto prima della conclusione
              dell'ordine.
            </p>
          </section>

          {/* 4 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              4. Account Utente
            </h2>

            <p>
              Alcune funzionalità del Servizio richiedono la creazione
              di un account.
            </p>

            <p className="mt-3">
              L'utente si impegna a:
            </p>

            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>fornire informazioni accurate e aggiornate;</li>
              <li>mantenere riservate le proprie credenziali;</li>
              <li>proteggere il proprio account da accessi non autorizzati;</li>
              <li>non condividere il proprio account con soggetti non autorizzati;</li>
              <li>informare tempestivamente Mytharion di eventuali accessi non autorizzati;</li>
              <li>non utilizzare account appartenenti ad altri utenti senza autorizzazione.</li>
            </ul>

            <p className="mt-3">
              L'utente è responsabile delle attività effettuate tramite
              il proprio account nella misura consentita dalla legge.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              5. Acquisti e Pagamenti
            </h2>

            <p>
              I prezzi dei prodotti e dei servizi sono indicati sul sito
              prima della conclusione dell'acquisto.
            </p>

            <p className="mt-3">
              Il pagamento può essere effettuato tramite fornitori di
              pagamento di terze parti, inclusi Stripe e PayPal.
            </p>

            <p className="mt-3">
              Mytharion non memorizza direttamente i dati completi delle
              carte di pagamento. I dati necessari per elaborare il
              pagamento possono essere trattati dai rispettivi fornitori
              secondo le loro condizioni e informative sulla privacy.
            </p>

            <p className="mt-3">
              L'utente è responsabile dell'utilizzo di un metodo di
              pagamento valido e autorizzato.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              6. Acquisti Una Tantum
            </h2>

            <p>
              Alcuni prodotti possono essere acquistati mediante un
              pagamento una tantum.
            </p>

            <p className="mt-3">
              Salvo diversa indicazione, il pagamento concede all'utente
              una licenza personale, non esclusiva e non trasferibile
              per utilizzare il prodotto secondo le caratteristiche
              dell'offerta acquistata.
            </p>

            <p className="mt-3">
              Quando espressamente indicato nella pagina del prodotto,
              gli aggiornamenti possono essere inclusi per un periodo
              determinato, ad esempio 12 mesi dalla data dell'acquisto.
            </p>

            <p className="mt-3">
              Eventuali periodi di aggiornamento inclusi non limitano
              i diritti inderogabili relativi alla conformità del
              contenuto o del servizio digitale previsti dalla normativa
              applicabile.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              7. Abbonamento Cartomancer Premium
            </h2>

            <p>
              Alcune funzionalità possono essere disponibili tramite
              un abbonamento mensile denominato "Cartomancer Premium".
            </p>

            <p className="mt-3">
              Salvo diversa indicazione nella pagina di acquisto,
              l'abbonamento:
            </p>

            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>viene fatturato mensilmente;</li>
              <li>si rinnova automaticamente ogni mese;</li>
              <li>rimane attivo fino alla cancellazione del rinnovo;</li>
              <li>
                può essere cancellato dall'utente secondo le modalità
                disponibili nella dashboard.
              </li>
            </ul>

            <p className="mt-3">
              La cancellazione impedisce i successivi rinnovi, ma non
              comporta automaticamente il rimborso del periodo di
              abbonamento già iniziato.
            </p>

            <p className="mt-3">
              L'accesso alle funzionalità Premium normalmente rimane
              disponibile fino alla scadenza del periodo già pagato,
              salvo quanto diversamente previsto dalla legge o in caso
              di sospensione per violazione dei presenti Termini.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              8. Periodi di Prova e Promozioni
            </h2>

            <p>
              Mytharion può offrire periodi di prova gratuiti o promozioni
              secondo le condizioni indicate nella relativa offerta.
            </p>

            <p className="mt-3">
              Salvo diversa indicazione, dopo l'acquisto di Cartomancer
              Basic può essere disponibile un periodo gratuito di 7 giorni
              di Cartomancer Premium.
            </p>

            <p className="mt-3">
              Il periodo di prova è utilizzabile una sola volta per ordine
              e non è trasferibile.
            </p>

            <p className="mt-3">
              Salvo diversa indicazione espressa nell'offerta, il termine
              del periodo di prova non comporta automaticamente l'addebito
              di un abbonamento Premium.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              9. Politica di Rimborso
            </h2>

            <p>
              Mytharion non offre rimborsi commerciali volontari per gli
              acquisti una tantum o per i periodi di abbonamento già
              iniziati, salvo diversa indicazione espressa nell'offerta
              o nei casi in cui un rimborso sia obbligatorio ai sensi
              della normativa applicabile.
            </p>

            <p className="mt-3">
              La cancellazione di un abbonamento Premium interrompe i
              successivi rinnovi e, di per sé, non comporta il rimborso
              del periodo di abbonamento già pagato.
            </p>

            <p className="mt-3">
              La presente politica non limita i diritti inderogabili
              riconosciuti dalla legge al consumatore.
            </p>

            <p className="mt-3">
              In particolare, restano impregiudicati gli eventuali diritti
              relativi al recesso, alla mancata fornitura, alla non
              conformità del contenuto o del servizio digitale e agli
              altri rimedi previsti dalla normativa applicabile.
            </p>

            <p className="mt-3">
              Pertanto, la dicitura "nessun rimborso" deve essere intesa
              esclusivamente come assenza di una politica commerciale
              volontaria di rimborso e non come esclusione dei diritti
              previsti obbligatoriamente dalla legge.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              10. Diritto di Recesso e Fornitura Immediata
            </h2>

            <p>
              Quando l'utente agisce in qualità di consumatore, il diritto
              di recesso si applica nei casi e secondo le modalità previste
              dalla normativa applicabile.
            </p>

            <p className="mt-3">
              Per i servizi digitali e per i contenuti digitali forniti
              senza supporto materiale, il diritto di recesso può essere
              soggetto alle eccezioni previste dalla legge.
            </p>

            <p className="mt-3">
              In particolare, quando la normativa applicabile lo consente,
              la fornitura di un contenuto digitale può iniziare prima
              della scadenza del periodo di recesso a seguito del consenso
              espresso dell'utente e del riconoscimento delle conseguenze
              previste dalla legge.
            </p>

            <p className="mt-3">
              La semplice presenza della presente clausola nei Termini
              non costituisce, da sola, una rinuncia generale al diritto
              di recesso. Eventuali consensi o richieste espressamente
              richiesti dalla normativa saranno raccolti con le modalità
              previste dalla legge, ove applicabili.
            </p>

            <p className="mt-3">
              Nulla nei presenti Termini deve essere interpretato come
              esclusione di un diritto di recesso che la legge non consenta
              di escludere.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              11. Conformità dei Contenuti e Servizi Digitali
            </h2>

            <p>
              Mytharion fornisce i propri contenuti e servizi digitali
              secondo quanto descritto nella relativa offerta e nel
              contratto, nel rispetto dei requisiti di conformità previsti
              dalla normativa applicabile.
            </p>

            <p className="mt-3">
              In caso di mancata fornitura o di difetto di conformità,
              il consumatore può avere diritto ai rimedi previsti dalla
              legge.
            </p>

            <p className="mt-3">
              Tali rimedi possono comprendere, a seconda delle circostanze
              e delle condizioni previste dalla normativa applicabile,
              il ripristino della conformità, una riduzione proporzionata
              del prezzo o la risoluzione del contratto.
            </p>

            <p className="mt-3">
              Nessuna disposizione dei presenti Termini limita o esclude
              tali diritti inderogabili.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              12. Licenza Software
            </h2>

            <p>
              L'acquisto di un prodotto non trasferisce all'utente la
              proprietà del software né dei relativi diritti di proprietà
              intellettuale.
            </p>

            <p className="mt-3">
              Salvo diversa indicazione, la licenza concessa è:
            </p>

            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>personale;</li>
              <li>non esclusiva;</li>
              <li>non trasferibile;</li>
              <li>limitata all'utilizzo consentito dal prodotto acquistato.</li>
            </ul>

            <p className="mt-4 font-semibold text-foreground">
              È vietato, salvo quanto consentito dalla legge:
            </p>

            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>rivendere o distribuire il software;</li>
              <li>condividere l'accesso con soggetti non autorizzati;</li>
              <li>copiare il software al di fuori degli usi consentiti;</li>
              <li>
                effettuare reverse engineering, decompilazione o
                disassemblaggio, nei limiti in cui tali attività siano
                vietate dalla legge applicabile;
              </li>
              <li>rimuovere copyright, marchi o protezioni tecniche;</li>
              <li>utilizzare il software per finalità illecite.</li>
            </ul>
          </section>

          {/* 13 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              13. Condotta dell'Utente
            </h2>

            <p>
              L'utente non può utilizzare il Servizio per:
            </p>

            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>violare leggi o regolamenti;</li>
              <li>danneggiare o sovraccaricare i sistemi;</li>
              <li>tentare di ottenere accesso non autorizzato;</li>
              <li>aggirare misure di sicurezza;</li>
              <li>raccogliere dati personali di altri utenti senza una base legittima;</li>
              <li>impersonare altre persone o soggetti;</li>
              <li>distribuire malware o codice dannoso;</li>
              <li>utilizzare il Servizio per attività fraudolente o abusive.</li>
            </ul>
          </section>

          {/* 14 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              14. Proprietà Intellettuale
            </h2>

            <p>
              Il software, il codice, il design, l'interfaccia, il logo,
              i testi, le grafiche, i marchi e gli altri elementi del
              Servizio sono di proprietà di Mytharion o sono utilizzati
              sulla base di diritti validi.
            </p>

            <p className="mt-3">
              Nessuna disposizione dei presenti Termini trasferisce
              all'utente diritti di proprietà intellettuale sul Servizio,
              salvo la licenza d'uso espressamente concessa.
            </p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              15. Disponibilità del Servizio
            </h2>

            <p>
              Mytharion si impegna ragionevolmente a mantenere il Servizio
              operativo, ma non garantisce che esso sia sempre disponibile,
              privo di errori o completamente ininterrotto.
            </p>

            <p className="mt-3">
              Il Servizio può essere temporaneamente sospeso per
              manutenzione, aggiornamenti, sicurezza, problemi tecnici
              o altre circostanze ragionevoli.
            </p>

            <p className="mt-3">
              Eventuali indisponibilità non limitano i diritti inderogabili
              del consumatore previsti dalla normativa applicabile.
            </p>
          </section>

          {/* 16 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              16. Modifiche al Servizio
            </h2>

            <p>
              Mytharion può aggiornare, modificare o introdurre nuove
              funzionalità nel Servizio.
            </p>

            <p className="mt-3">
              Le modifiche possono essere necessarie per motivi tecnici,
              di sicurezza, normativi o per migliorare il Servizio.
            </p>

            <p className="mt-3">
              Per i servizi digitali forniti continuativamente, eventuali
              modifiche sostanziali saranno effettuate e comunicate nel
              rispetto della normativa applicabile.
            </p>

            <p className="mt-3">
              Qualora una modifica incida negativamente sui diritti
              dell'utente in un modo rilevante e la legge riconosca un
              diritto di recesso o altro rimedio, tale diritto rimane
              impregiudicato.
            </p>
          </section>

          {/* 17 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              17. Sospensione e Terminazione dell'Account
            </h2>

            <p>
              Mytharion può sospendere o terminare un account quando
              ragionevolmente necessario, ad esempio in caso di violazione
              dei presenti Termini, frode, abuso, rischio per la sicurezza
              o obbligo legale.
            </p>

            <p className="mt-3">
              Quando ragionevolmente possibile, Mytharion può informare
              l'utente prima della sospensione o della terminazione.
            </p>

            <p className="mt-3">
              L'utente può richiedere la cancellazione del proprio account
              secondo le modalità indicate nella Privacy Policy.
            </p>
          </section>

          {/* 18 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              18. Limitazione di Responsabilità
            </h2>

            <p>
              Nella misura consentita dalla legge, Mytharion non è
              responsabile per danni derivanti da eventi non ragionevolmente
              sotto il proprio controllo, inclusi guasti di rete,
              indisponibilità di servizi di terzi, attacchi informatici
              non ragionevolmente prevedibili o eventi di forza maggiore.
            </p>

            <p className="mt-3">
              Mytharion non esclude né limita la propria responsabilità
              nei casi in cui tale esclusione o limitazione sia vietata
              dalla legge.
            </p>

            <p className="mt-3">
              In particolare, nulla nei presenti Termini limita i diritti
              inderogabili del consumatore relativi a conformità,
              recesso, garanzia legale o altri rimedi previsti dalla legge.
            </p>

            <p className="mt-3">
              Nella misura massima consentita dalla legge, eventuali
              responsabilità risarcibili di Mytharion possono essere
              limitate all'importo effettivamente pagato dall'utente
              per il prodotto o servizio oggetto della controversia,
              salvo nei casi in cui tale limitazione non sia legalmente
              consentita.
            </p>
          </section>

          {/* 19 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              19. Modifiche ai Termini
            </h2>

            <p>
              Mytharion può modificare i presenti Termini per motivi
              legali, tecnici, commerciali o operativi.
            </p>

            <p className="mt-3">
              In caso di modifiche sostanziali, saranno adottate misure
              ragionevoli per informare gli utenti interessati quando
              richiesto dalla normativa applicabile.
            </p>

            <p className="mt-3">
              La versione aggiornata sarà pubblicata sul sito con la
              relativa data di aggiornamento.
            </p>

            <p className="mt-3">
              Le modifiche non limitano i diritti inderogabili già
              maturati dall'utente.
            </p>
          </section>

          {/* 20 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              20. Privacy e Protezione dei Dati
            </h2>

            <p>
              Il trattamento dei dati personali nell'ambito del Servizio
              è disciplinato dalla nostra{" "}
              <Link
                to="/privacy"
                className="text-gold hover:underline"
              >
                Privacy Policy
              </Link>.
            </p>

            <p className="mt-3">
              La Privacy Policy descrive le modalità di raccolta,
              utilizzo, conservazione e protezione dei dati personali
              e i diritti dell'interessato previsti dalla normativa
              applicabile.
            </p>
          </section>

          {/* 21 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              21. Legge Applicabile e Foro Competente
            </h2>

            <p>
              I presenti Termini sono regolati dalla legge italiana,
              fatti salvi i diritti inderogabili riconosciuti ai
              consumatori dalla normativa applicabile.
            </p>

            <p className="mt-3">
              Per gli utenti che agiscono in qualità di consumatori,
              eventuali controversie saranno sottoposte al foro
              competente individuato secondo la normativa applicabile
              a tutela dei consumatori.
            </p>

            <p className="mt-3">
              Per gli utenti che non agiscono in qualità di consumatori,
              nella misura consentita dalla legge, il foro competente
              sarà quello di Napoli, Italia.
            </p>
          </section>

          {/* 22 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              22. Disposizioni Finali
            </h2>

            <p>
              Qualora una disposizione dei presenti Termini venga ritenuta
              invalida o inapplicabile, le restanti disposizioni rimarranno
              valide ed efficaci nella misura consentita dalla legge.
            </p>

            <p className="mt-3">
              Il mancato esercizio di un diritto previsto dai presenti
              Termini non costituisce rinuncia a tale diritto.
            </p>

            <p className="mt-3">
              I presenti Termini, insieme alle eventuali condizioni
              specifiche relative ai singoli prodotti e alla Privacy Policy,
              costituiscono il quadro contrattuale applicabile all'utilizzo
              del Servizio, salvo quanto diversamente previsto dalla legge.
            </p>
          </section>

          {/* 23 */}
          <section>
            <h2 className="font-heading text-2xl text-foreground mb-4">
              23. Contatti
            </h2>

            <p>
              Per domande o comunicazioni relative ai presenti Termini:
            </p>

            <p className="mt-3">
              <strong>Email:</strong>{" "}
              <a
                href="mailto:legal@mytharion.app"
                className="text-gold hover:underline"
              >
                legal@mytharion.app
              </a>
            </p>

            <p className="mt-2">
              <strong>Sede:</strong> Napoli, Italia
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border">
          <Link
            to="/"
            className="text-gold hover:text-gold/80 text-sm"
          >
            ← Torna alla Home
          </Link>
        </div>

      </div>
    </div>
  );
};

export default TermsPage;
